/**
 * The API's daily AI limits (meu-treino-api src/api/gpt/utils/ai-limits.js, docs/ai-limits.md). A request turned down
 * comes back as a GraphQL error with `extensions`: { code: 'AI_DAILY_LIMIT' | 'AI_RATE_LIMIT', kind, scope, limit,
 * resetAt }. Neither code is an auth error, so the error link (main.jsx) never signs anyone out for one.
 */

const LIMIT_CODES = ['AI_DAILY_LIMIT', 'AI_RATE_LIMIT'];

// The limits in the person's terms (8 splits, not 48 calls), for an answer that doesn't carry `limit`
const SHOWN_LIMITS = { workout: 20, split: 8, dietDay: 5, mealSwap: 30, import: 5, points: 10 };

const DAY_MS = 24 * 60 * 60 * 1000;

/** The limit a request was turned down for ({ code, kind, scope, limit, resetAt, isDaily }), or null for any other error. */
export const aiLimitOf = (error) => {
  const extensions = (error?.graphQLErrors || []).map((graphQLError) => graphQLError?.extensions).find((ext) => LIMIT_CODES.includes(ext?.code));
  if (!extensions) return null;
  const { code, kind, scope, limit, resetAt } = extensions;
  return { code, kind, scope, limit, resetAt, isDaily: code === 'AI_DAILY_LIMIT' };
};

const startOfDay = (date) => new Date(date.getFullYear(), date.getMonth(), date.getDate()).getTime();

/**
 * When a daily limit lifts, on the person's clock: 'tomorrow' (midnight), or 'today' / 'tomorrowAt' with the time. The
 * day ends at midnight in São Paulo, which is midnight across most of Brazil but another hour elsewhere.
 */
const whenItLifts = (resetAt, locale, now) => {
  const reset = new Date(resetAt);
  if (!resetAt || Number.isNaN(reset.getTime())) return { when: 'tomorrow' };
  const days = Math.round((startOfDay(reset) - startOfDay(now)) / DAY_MS);
  const time = reset.toLocaleTimeString(locale, { hour: '2-digit', minute: '2-digit' });
  if (days <= 0) return { when: 'today', time };
  const isMidnight = reset.getHours() === 0 && reset.getMinutes() === 0;
  return days === 1 && isMidnight ? { when: 'tomorrow' } : { when: 'tomorrowAt', time };
};

const RESET_KEYS = { today: 'resetToday', tomorrow: 'resetTomorrow', tomorrowAt: 'resetTomorrowAt' };
const RESUME_KEYS = { today: 'resumeToday', tomorrow: 'resumeTomorrow', tomorrowAt: 'resumeTomorrowAt' };

/**
 * What to tell the person about a limit, where the action happened:
 * - title and body: for a screen or a box (the workout builder, the week being built);
 * - message: the same in one line (a swapped meal, the plan's first day);
 * - resume: in place of a half-built week's button ("Continue amanhã");
 * - canRetry: only after the burst, a minute's wait. A daily limit lifts on its own, so there's nothing to retry.
 */
export const aiLimitText = (t, limit, { locale, now = new Date() } = {}) => {
  const say = (key, options) => t(`components.AiLimits.${key}`, options);

  if (!limit.isDaily) {
    return { title: say('burstTitle'), body: say('burstBody'), message: say('burst'), resume: null, canRetry: true };
  }

  const what = limit.scope !== 'total' && SHOWN_LIMITS[limit.kind] ? limit.kind : 'total';
  const reached = what === 'total' ? say('daily.total') : say(`daily.${what}`, { count: limit.limit ?? SHOWN_LIMITS[what] });
  const { when, time } = whenItLifts(limit.resetAt, locale, now);
  const lifts = time ? say(RESET_KEYS[when], { time }) : say(RESET_KEYS[when]);
  const resume = time ? say(RESUME_KEYS[when], { time }) : say(RESUME_KEYS[when]);

  return { title: say('dailyTitle'), body: `${reached} ${lifts}`, message: `${reached} ${lifts}`, resume, canRetry: false };
};
